import { Col, Row } from 'react-styled-flexboxgrid';

import Arrow from './ui/icons/Arrow';
import Image from 'next/image';
import Link from 'next/link';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { rem } from 'polished';
import styled from 'styled-components';
import { theme } from 'styled-tools';

import imageUrlBuilder from '@sanity/image-url';
import sanityClient from '../lib/sanity';

const StyledBlogPosts = styled(Row)`
  justify-content: space-between;
  margin-bottom: 2rem;

  a {
    color: ${theme('colors.blue')};
  }
`;

const FigCaption = styled('figcaption')`
  margin: 1em 0;
  font-weight: 600;
  font-size: ${rem(16)};
  line-height: ${rem(26)};
`;

const Aside = styled.aside`
  border-bottom: 4px solid ${theme('colors.blue')};

  .post-image {
    img {
      object-fit: cover;
    }
  }
`;

const LinkInline = styled.p`
  color: ${theme('colors.orange')};
  text-transform: uppercase;
  font-weight: 700;
  font-size: ${rem(18)};
  :hover {
    color: ${theme('colors.blue')};
    svg,
    path {
      fill: ${theme('colors.blue')};
      stroke: ${theme('colors.blue')};
    }
  }
`;

interface BlogProps {
  posts?: unknown[];
  withReadmore?: boolean;
}

const builder = imageUrlBuilder(sanityClient);

function urlFor(source) {
  return builder.image(source);
}

const BlogPosts: React.FC<BlogProps> = ({ posts = [], withReadmore = true }) => {
  const [maxHeight, setMaxHeight] = useState(0);
  const figRefs = useRef([]);

  useEffect(() => {
    const heights = figRefs.current.map((ref) => ref.offsetHeight);
    const maxHeight = Math.max(...heights);
    setMaxHeight(maxHeight);
  }, [posts]);

  return (
    <StyledBlogPosts>
      {posts.map((post: unknown, index) => {
        const typedPost = post as {
          _key: string;
          slug: { current: string };
          title: string;
          mainImage: { image: unknown };
        };
        return (
          <Col key={`${typedPost._key}-${index}`} xs={12} sm={4} style={{ zIndex: 80, maxWidth: '795px' }}>
            <Aside>
              <Link href={`/blog/${typedPost.slug.current || '#'}`} aria-label={`Read more about ${typedPost.title}`}>
                {typedPost.mainImage && (
                  <figure>
                    <Image
                      src={urlFor(typedPost.mainImage.image).width(795).height(530).auto('format').quality(80).url()}
                      alt={typedPost.title}
                      width={795}
                      height={530}
                    />
                    <FigCaption
                      ref={(el) => {
                        figRefs.current[index] = el;
                      }}
                      style={{
                        height: maxHeight ? `${maxHeight}px` : 'auto',
                      }}
                    >
                      {typedPost.title}
                    </FigCaption>
                  </figure>
                )}
                {withReadmore && (
                  <LinkInline>
                    Read More <Arrow style={{ marginLeft: '15px' }} />
                  </LinkInline>
                )}
              </Link>
            </Aside>
          </Col>
        );
      })}
    </StyledBlogPosts>
  );
};

BlogPosts.propTypes = {
  posts: PropTypes.array,
};

export default BlogPosts;
